import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './image-upload.scss';
import { compressImages } from '../../utils/imageCompression';
import { getDocumentUploadPlaceholderSrc } from '../../utils/documentUploadPlaceholder';

// Standardized output resolution for all camera captures.
// Ensures consistent image dimensions accepted by the backend.
const CAPTURE_MAX_SIDE = 1280;
const CAPTURE_QUALITY = 0.85;

interface ImageUploadProps {
  onImagesUploaded: (files: File[]) => void | Promise<void>;
  maxImages?: number;
  title?: string; // 🔵 NEW
}

/** Start camera with back camera (environment) preferred - avoids native file input which ignores facing on some mobiles */
async function startBackCamera(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
  } catch {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
    } catch {
      return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } } });
    }
  }
}

export default function ImageUpload({ 
  onImagesUploaded, 
  maxImages = 5,
  title = "رفع الصور" // 🔵 default fallback
}: ImageUploadProps) {

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  const placeholderSrc = useMemo(() => getDocumentUploadPlaceholderSrc(title), [title]);

  const addFiles = useCallback(
    async (files: File[]) => {
      if (uploadedFiles.length + files.length > maxImages) {
        alert(`يمكنك رفع ${maxImages} صور كحد أقصى`);
        return;
      }
      try {
        const compressedFiles = await compressImages(files, {
          maxSizeMB: 2,
          maxWidthOrHeight: CAPTURE_MAX_SIDE,
          quality: CAPTURE_QUALITY,
        });
        const newFiles = [...uploadedFiles, ...compressedFiles];
        setUploadedFiles(newFiles);
        const newPreviews = compressedFiles.map(file => URL.createObjectURL(file));
        setPreviews(prev => [...prev, ...newPreviews]);
        await onImagesUploaded(newFiles);
      } catch (error) {
        console.error('❌ Error compressing images:', error);
        const newFiles = [...uploadedFiles, ...files];
        setUploadedFiles(newFiles);
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviews(prev => [...prev, ...newPreviews]);
        await onImagesUploaded(newFiles);
      }
    },
    [uploadedFiles, maxImages, onImagesUploaded]
  );

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    await addFiles(files);
    if (event.target) event.target.value = '';
  };

  const removeImage = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    URL.revokeObjectURL(previews[index]);
    setUploadedFiles(newFiles);
    setPreviews(newPreviews);
    onImagesUploaded(newFiles);
  };

  // Start back camera when capture modal opens; stop on close
  useEffect(() => {
    if (!showCameraCapture) return;
    setCameraError(null);
    let stream: MediaStream | null = null;
    startBackCamera()
      .then((s) => {
        stream = s;
        cameraStreamRef.current = s;
        const video = cameraVideoRef.current;
        if (video) {
          video.srcObject = s;
          video.play().catch(() => {});
        }
      })
      .catch((err) => {
        console.error('Camera error:', err);
        setCameraError(err?.message || 'لا يمكن فتح الكاميرا');
      });
    return () => {
      stream?.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
      if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    };
  }, [showCameraCapture]);

  const handleCapturePhoto = useCallback(() => {
    const video = cameraVideoRef.current;
    const stream = cameraStreamRef.current;
    if (!video || !stream || video.videoWidth === 0) return;

    // Scale to fit within CAPTURE_MAX_SIDE while maintaining aspect ratio
    const srcW = video.videoWidth;
    const srcH = video.videoHeight;
    const scale = Math.min(CAPTURE_MAX_SIDE / srcW, CAPTURE_MAX_SIDE / srcH, 1);
    const canvasW = Math.round(srcW * scale);
    const canvasH = Math.round(srcH * scale);

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvasW, canvasH);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        addFiles([file]).then(() => {
          setShowCameraCapture(false);
        });
      },
      'image/jpeg',
      CAPTURE_QUALITY
    );
  }, [addFiles]);

  const closeCameraCapture = useCallback(() => {
    setShowCameraCapture(false);
    setCameraError(null);
  }, []);

  return (
    <div className="image-upload-container">
      <div className="upload-header">
        <h3>{title}</h3> {/* 🔵 Replace old static text */}
        <p className="upload-count">{uploadedFiles.length} / {maxImages}</p>
      </div>

      <div className="upload-actions">
        <button 
          className="upload-button gallery"
          onClick={() => galleryInputRef.current?.click()}
          disabled={uploadedFiles.length >= maxImages}
        >
          <span className="material-symbols-outlined">photo_library</span>
          اختر من المعرض
        </button>

        <button 
          className="upload-button camera"
          onClick={() => setShowCameraCapture(true)}
          disabled={uploadedFiles.length >= maxImages}
        >
          <span className="material-symbols-outlined">photo_camera</span>
          التقط صورة
        </button>
      </div>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple={maxImages > 1}
        onChange={handleFileSelect}
        onClick={(e) => (e.currentTarget.value = '')}
        style={{ display: 'none' }}
      />

      {/* Custom camera capture uses back camera (getUserMedia). Native input kept as fallback only. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={maxImages > 1}
        onChange={handleFileSelect}
        onClick={(e) => (e.currentTarget.value = '')}
        style={{ display: 'none' }}
      />

      {showCameraCapture && (
        <div className="image-upload-camera-overlay" role="dialog" aria-label="التقط صورة">
          <div className="image-upload-camera-modal">
            <div className="image-upload-camera-header">
              <span>الكاميرا الخلفية</span>
              <button type="button" className="image-upload-camera-close" onClick={closeCameraCapture} aria-label="إغلاق">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            {cameraError ? (
              <div className="image-upload-camera-error">
                <p>{cameraError}</p>
                <button type="button" className="upload-button gallery" onClick={closeCameraCapture}>إغلاق</button>
              </div>
            ) : (
              <>
                <video
                  ref={cameraVideoRef}
                  className="image-upload-camera-video"
                  playsInline
                  muted
                  autoPlay
                />
                <div className="image-upload-camera-actions">
                  <button type="button" className="upload-button gallery" onClick={closeCameraCapture}>إلغاء</button>
                  <button type="button" className="upload-button camera" onClick={handleCapturePhoto}>التقط</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {previews.length > 0 && (
        <div className="preview-grid">
          {previews.map((preview, index) => (
            <div key={index} className="preview-item">
              <img src={placeholderSrc ?? preview} alt="" />
              <button 
                className="remove-button"
                onClick={() => removeImage(index)}
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
